import { BadRequestError } from '_core/api/client';
import { useApiClient } from '_core/api/context';
import { fetchAllPages } from '_core/api/fetchAllPages';
import { DATE_FORMAT_API_DATE } from '_core/dates/formats';
import { createDateRange } from '_core/dates/utils';
import { useDialog } from '_core/dialogs/useDialog';
import { CenteredSpinner } from '_core/feedback/centeredSpinner';
import { EmptyListMessage } from '_core/feedback/emptyListMessage';
import { GenericErrorMessage } from '_core/feedback/genericErrorMessage';
import { DateRangeWithMonthSelectFilter } from '_core/filters/dateRangeWithMonthSelect';
import { submissionErrorsFromApiError } from '_core/final-form/submissionErrorsFromApiError';
import { SearchForm } from '_core/forms/searchForm';
import { uniq } from '_core/fp/uniq';
import { indexByLastItem } from '_core/indexBy';
import { Switch } from '_core/inputs/switch';
import { LegendItem } from '_core/legend';
import { FormattedTitle } from '_core/react-head/formattedTitle';
import { Region } from '_core/react-window/baseTable';
import { TreeTableNode } from '_core/react-window/types';
import {
  everyNode,
  findNode,
  flattenNodesToRows,
  setAllNodesIsExpanded,
  updateNodes,
} from '_core/react-window/utils';
import { LinkButton } from '_core/router5/linkButton';
import { useToaster } from '_core/toaster/toasterContext';
import { Toolbar } from '_core/toolbar';
import { useAsyncData } from '_core/useAsyncData';
import { AnchorButton, Button, Intent, Spinner } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import dayjs from 'dayjs';
import { Col, Grid, Row, VGrid } from 'layout/contentLayout';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useRouteNode } from 'react-router5';
import {
  acceptRentWagonSellWagons,
  closeRentWagonSellWagons,
  fetchRentWagonSellList,
  fetchRentWagonSellTree,
  IUpdateRentWagonSellPausesItem,
  makeShipmentForPeriod,
  RentWagonSell,
  RentWagonSellListItem,
  RentWagonSellPause,
  RentWagonSellPauseCause,
  RentWagonSellState,
  // startRentWagonSellCalculation,
  // startRentWagonSellRepair,
  // stopRentWagonSellCalculation,
  // stopRentWagonSellRepair,
  updateRentWagonSellPauses,
} from 'rentWagonSell/api';
import invariant from 'tiny-invariant';
import { WagonNumbersFilter } from 'wagons/numbersFilter';

import { RentWagonColor } from '../colors';
import {
  EditPausesDialogForm,
  IEditPausesDialogFormValues,
} from '../editPausesDialogForm';
import {
  DATE_COLUMN_START_INDEX,
  findPausesForRegions,
  RentWagonsTreeTable,
  RentWagonsTreeTableNodeData,
  RentWagonsTreeTableProtocolNodeData,
  RentWagonsTreeTableRentWagonNodeData,
  RentWagonsTreeTableSupplierNodeData,
} from '../treeTable';
import {
  IRentWagonsSellAcceptDialogFormValues,
  RentWagonsSellAcceptDialogForm,
} from './acceptDialogForm';
import {
  IRentWagonsSellCloseDialogFormValues,
  RentWagonsSellCloseDialogForm,
} from './closeDialogForm';

function createPausesFromRegions(
  selectedRegions: Region[],
  dates: Date[],
  nodes: Array<
    TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
  >,
  cause: RentWagonSellPauseCause
) {
  const result: IUpdateRentWagonSellPausesItem[] = [];

  const nodeRows = flattenNodesToRows(nodes);

  for (const { cols, rows } of selectedRegions) {
    if (!cols || !rows) {
      return [];
    }

    if (cols[0] < DATE_COLUMN_START_INDEX) {
      return [];
    }

    for (let rowIndex = rows[0]; rowIndex < rows[1] + 1; rowIndex++) {
      const nodeRow = nodeRows[rowIndex];

      if (nodeRow.type !== 'node' || nodeRow.node.data.kind !== 'rentWagon') {
        return [];
      }

      const { rentWagon } = nodeRow.node.data;

      let rent = result.find(r => r.id === rentWagon.id);

      if (!rent) {
        rent = { id: rentWagon.id, pauses: [] };
        result.push(rent);
      }

      rent.pauses.push(
        new RentWagonSellPause({
          cause,
          note: '',
          startDate: dates[cols[0] - DATE_COLUMN_START_INDEX],
          endDate: dates[cols[1] - DATE_COLUMN_START_INDEX],
          isCharging: true,
          authorModified: null,
          modified: null,
          duration: null,
        })
      );
    }
  }

  return result;
}

export default function RentWagonsSellListRoute() {
  const { route, router } = useRouteNode('rentWagons.sell');
  const api = useApiClient();
  const toaster = useToaster();

  const dateRangeFilterValue = useMemo(
    () => ({
      startDate: (route.params.fromDate
        ? dayjs(route.params.fromDate)
        : dayjs().startOf('month')
      ).toDate(),
      endDate: (route.params.toDate
        ? dayjs(route.params.toDate)
        : dayjs().endOf('month').startOf('day')
      ).toDate(),
    }),

    [route.params.fromDate, route.params.toDate]
  );

  const [showInactive, setInactive] = React.useState<boolean>(true);

  const states = useMemo(() => {
    const result = Object.values(RentWagonSellState).filter(
      state => state !== RentWagonSellState.Inactive
    );

    if (showInactive) {
      result.push(RentWagonSellState.Inactive);
    }

    return result;
  }, [showInactive]);

  const commonFilterQuery = useMemo(
    () => ({
      fromDate: dateRangeFilterValue.startDate,
      search: route.params.search as string | undefined,
      states,
      toDate: dateRangeFilterValue.endDate,
      wagonNumbers:
        route.params.wagonNumbers != null
          ? (route.params.wagonNumbers as string).split(',')
          : undefined,
    }),
    [dateRangeFilterValue, route, states]
  );

  const protocolIdToExpand = useMemo(
    () =>
      route.params.expandProtocol == null
        ? null
        : Number(route.params.expandProtocol),
    [route.params.expandProtocol]
  );

  const acceptDialog = useDialog<{
    initialValues: IRentWagonsSellAcceptDialogFormValues;
  }>();
  const closeDialog = useDialog<{
    initialValues: IRentWagonsSellCloseDialogFormValues;
  }>();

  function applyFilterParams(
    filterParams: Record<string, number | string | undefined>
  ) {
    router.navigate(route.name, {
      ...route.params,
      page: undefined,
      ...filterParams,
    });
  }

  const [selectedNodes, setSelectedNodes] = useState<
    Array<TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>>
  >([]);

  const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);

  const [isBatchUpdateInProgress, setIsBatchUpdateInProgress] = useState(false);

  const [isShipmentButtonActive, setShipmentButtonActive] =
    useState<boolean>(true);

  const [shipmentId, setShipmentId] = useState<null | string | number>(null);

  const isBulkUpdatesPausesDisabled =
    isBatchUpdateInProgress || selectedRegions.length === 0;

  const batchUpdate = useCallback(
    async (updater: () => Promise<void>) => {
      try {
        setIsBatchUpdateInProgress(true);
        await updater();
      } catch (err) {
        if (err instanceof BadRequestError && err.message) {
          toaster.show({
            icon: 'error',
            intent: Intent.DANGER,
            message: err.message,
          });
        } else {
          toaster.show({
            icon: 'error',
            intent: Intent.DANGER,
            message: 'Что-то пошло не так',
          });

          throw err;
        }
      } finally {
        setIsBatchUpdateInProgress(false);
      }
    },
    [toaster]
  );

  const [dateRangeToRender, setDateRangeToRender] =
    useState(dateRangeFilterValue);

  const { data, isFetching, refetch, updateData } = useAsyncData(
    [commonFilterQuery, protocolIdToExpand, api],
    async () => {
      const treeResponse = await fetchRentWagonSellTree(api, commonFilterQuery);

      return {
        meta: treeResponse.meta,

        nodes: treeResponse.results.map(
          (
            supplier
          ): TreeTableNode<
            RentWagonsTreeTableNodeData<RentWagonSellListItem>
          > => {
            const prevSupplierNode = data
              ? findNode(
                  data.nodes,
                  node =>
                    node.data.kind === 'supplier' &&
                    node.data.supplierId === supplier.supplier
                )
              : undefined;

            const supplierIsExpanded =
              prevSupplierNode && prevSupplierNode.children
                ? prevSupplierNode.children.isExpanded
                : protocolIdToExpand != null &&
                  supplier.protocols.some(
                    protocol => protocol.id === protocolIdToExpand
                  );

            return {
              id: `supplier-${supplier.supplier}`,
              data: {
                kind: 'supplier',
                supplierId: supplier.supplier,
                supplierName: supplier.supplierName,
                protocolCount: supplier.protocols.length,
              },
              children: {
                isExpanded: supplierIsExpanded,
                nodes: supplier.protocols.map(
                  (
                    protocol
                  ): TreeTableNode<
                    RentWagonsTreeTableNodeData<RentWagonSellListItem>
                  > => {
                    const prevProtocolNode = data
                      ? findNode(
                          data.nodes,
                          node =>
                            node.data.kind === 'protocol' &&
                            node.data.protocolId === protocol.id
                        )
                      : undefined;

                    const protocolIsExpanded =
                      prevProtocolNode && prevProtocolNode.children
                        ? prevProtocolNode.children.isExpanded
                        : protocolIdToExpand != null &&
                          protocol.id === protocolIdToExpand;

                    return {
                      id: `protocol-${protocol.id}`,
                      data: {
                        kind: 'protocol',
                        contractId: protocol.contract,
                        protocolId: protocol.id,
                        protocolName: protocol.name,
                        rentWagonCount: protocol.rentsCount,
                        shipment: protocol.shipment,
                      },
                      children: {
                        isExpanded: protocolIsExpanded,
                        hasMoreChildNodesToFetch: true,
                        isFetching: false,
                        nodes: [],
                      },
                    };
                  }
                ),
              },
            };
          }
        ),
      };
    }
  );

  const lastDataRef = useRef(data);

  if (lastDataRef.current !== data) {
    setSelectedRegions([]);
    setSelectedNodes([]);
    setDateRangeToRender(dateRangeFilterValue);
    lastDataRef.current = data;
  }

  const nodes = data && data.nodes;

  const updateNodesState = useCallback(
    (
      mapper: (
        node: TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
      ) => TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
    ) => {
      updateData(
        prevData =>
          prevData && {
            ...prevData,
            nodes: updateNodes(prevData.nodes, mapper),
          }
      );
    },

    [updateData]
  );

  const bulkUpdatePauses = useCallback(
    (rents: IUpdateRentWagonSellPausesItem[]) =>
      batchUpdate(async () => {
        const updatedRentWagons = await updateRentWagonSellPauses(api, rents);

        const updatedRentWagonsById = indexByLastItem(
          updatedRentWagons,
          rentWagon => rentWagon.id
        );

        updateNodesState(node => {
          if (node.data.kind !== 'rentWagon') {
            return node;
          }

          const { rentWagon } = node.data;

          if (!(rentWagon.id in updatedRentWagonsById)) {
            return node;
          }

          const updatedRentWagon = updatedRentWagonsById[rentWagon.id];

          return {
            ...node,
            data: { ...node.data, rentWagon: updatedRentWagon },
          };
        });
      }),
    [batchUpdate, api, updateNodesState]
  );

  const datesToRender = useMemo(
    () =>
      createDateRange(
        'day',
        dateRangeToRender.startDate,
        dayjs(dateRangeToRender.endDate).add(1, 'day').toDate()
      ),
    [dateRangeToRender]
  );

  const datesColStart = DATE_COLUMN_START_INDEX;
  const datesColEnd = DATE_COLUMN_START_INDEX + datesToRender.length - 1;

  const isPauseCreationAllowed = useMemo(() => {
    if (isBulkUpdatesPausesDisabled) {
      return false;
    }

    const nodeRows = nodes ? flattenNodesToRows(nodes) : [];

    return selectedRegions.every(region => {
      const { cols, rows } = region;

      if (!cols || !rows) {
        return false;
      }

      if (cols[0] < datesColStart || cols[1] > datesColEnd) {
        return false;
      }

      if (rows[0] > nodeRows.length || rows[1] > nodeRows.length) {
        return false;
      }

      for (let rowIndex = rows[0]; rowIndex <= rows[1]; rowIndex++) {
        const nodeRow = nodeRows[rowIndex];

        if (nodeRow.type !== 'node' || nodeRow.node.data.kind !== 'rentWagon') {
          return false;
        }
      }

      return true;
    });
  }, [
    datesColStart,
    datesColEnd,
    isBulkUpdatesPausesDisabled,
    nodes,
    selectedRegions,
  ]);

  const createPausesOnSelectedRegions = (
    pauseCause: RentWagonSellPauseCause
  ) => {
    invariant(nodes);

    bulkUpdatePauses(
      createPausesFromRegions(
        selectedRegions,
        datesToRender,
        nodes,
        pauseCause
      ).map(rent => {
        const rentWagonNode = findNode(
          nodes,
          (
            node
          ): node is TreeTableNode<
            RentWagonsTreeTableNodeData<RentWagonSellListItem> & {
              kind: 'rentWagon';
            }
          > =>
            node.data.kind === 'rentWagon' && node.data.rentWagon.id === rent.id
        );

        if (!rentWagonNode) {
          return rent;
        }

        const { rentWagon } = rentWagonNode.data;

        return {
          ...rent,
          pauses: [...rentWagon.pauses, ...rent.pauses],
        };
      })
    );
  };

  const handleCreatePausesClick = () => {
    createPausesOnSelectedRegions(RentWagonSellPauseCause.Paused);
  };

  const handleCreateRoutineRepairsClick = () => {
    createPausesOnSelectedRegions(RentWagonSellPauseCause.RoutineRepair);
  };

  const handleCreateScheduledRepairsClick = () => {
    createPausesOnSelectedRegions(RentWagonSellPauseCause.ScheduledRepair);
  };

  const pausesForSelectedRegions = useMemo(
    () =>
      nodes
        ? findPausesForRegions(
            selectedRegions,
            nodes,
            dateRangeToRender.startDate,
            dateRangeToRender.endDate
          )
        : [],
    [dateRangeToRender, nodes, selectedRegions]
  );

  const isPauseEditingAllowed = useMemo(() => {
    if (isBulkUpdatesPausesDisabled) {
      return false;
    }

    return (
      pausesForSelectedRegions.length !== 0 &&
      pausesForSelectedRegions.every(
        pausesForRegion => pausesForRegion.found !== null
      )
    );
  }, [isBulkUpdatesPausesDisabled, pausesForSelectedRegions]);

  const editPausesDialogForm = useDialog<{
    initialValues: IEditPausesDialogFormValues;
  }>();

  const handleEditPausesClick = () => {
    editPausesDialogForm.open({ initialValues: { note: '' } });
  };

  const isPauseRemovalAllowed = useMemo(() => {
    if (isBulkUpdatesPausesDisabled) {
      return false;
    }

    return (
      pausesForSelectedRegions.length !== 0 &&
      pausesForSelectedRegions.every(
        pausesForRegion => pausesForRegion.found !== null
      )
    );
  }, [isBulkUpdatesPausesDisabled, pausesForSelectedRegions]);

  const handleRemovePausesClick = () => {
    if (!nodes) {
      return;
    }

    const indicesToRemove: Record<
      IUpdateRentWagonSellPausesItem['id'],
      { rentWagon: RentWagonSellListItem; pauseIndices: number[] }
    > = {};

    pausesForSelectedRegions.forEach(({ found }) => {
      if (!found) {
        return;
      }

      const rentWagonNode = findNode(
        nodes,
        (
          node
        ): node is TreeTableNode<
          RentWagonsTreeTableNodeData<RentWagonSellListItem> & {
            kind: 'rentWagon';
          }
        > =>
          node.data.kind === 'rentWagon' &&
          node.data.rentWagon.id === found.rentWagonId
      );

      if (!rentWagonNode) {
        return;
      }

      indicesToRemove[found.rentWagonId] = indicesToRemove[
        found.rentWagonId
      ] || { rentWagon: rentWagonNode.data.rentWagon, pauseIndices: [] };
      indicesToRemove[found.rentWagonId].pauseIndices.push(
        ...found.pauseIndices
      );
    });

    const rents = Object.values(indicesToRemove).map(
      ({ rentWagon, pauseIndices }): IUpdateRentWagonSellPausesItem => ({
        id: rentWagon.id,
        pauses: rentWagon.pauses.filter(
          (_pause, index) => !pauseIndices.includes(index)
        ),
      })
    );

    bulkUpdatePauses(rents);
  };

  const fetchChildNodes = useCallback(
    async (
      parentNodes: Array<
        TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
      >
    ) => {
      invariant(parentNodes.length === 2);
      const parentNode = parentNodes[parentNodes.length - 1];

      if (parentNode.data.kind === 'protocol') {
        const grandParentNode = parentNodes[parentNodes.length - 2];
        invariant(
          grandParentNode.data.kind === 'supplier',
          `Expected protocol node parent to be supplier, got: "${grandParentNode.data.kind}"`
        );

        const protocolId = parentNode.data.protocolId;
        const supplierId = grandParentNode.data.supplierId;

        const response = await fetchAllPages(page =>
          fetchRentWagonSellList(api, {
            ...commonFilterQuery,
            page,
            protocol: protocolId,
            supplier: supplierId,
          })
        );

        return response.results.map(
          (
            rentWagon
          ): TreeTableNode<
            RentWagonsTreeTableRentWagonNodeData<RentWagonSellListItem>
          > => ({
            data: { kind: 'rentWagon', rentWagon },
            id: `rentWagon-${rentWagon.id}`,
          })
        );
      }

      throw new Error(`Unexpected node kind: "${parentNode.data.kind}"`);
    },
    [commonFilterQuery, api]
  );

  const handleNodesChange = useCallback(
    (
      newNodesOrUpdater:
        | Array<
            TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
          >
        | ((
            prevNodes: Array<
              TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
            >
          ) => Array<
            TreeTableNode<RentWagonsTreeTableNodeData<RentWagonSellListItem>>
          >)
    ) => {
      updateData(
        prevData =>
          prevData && {
            ...prevData,
            nodes:
              typeof newNodesOrUpdater === 'function'
                ? newNodesOrUpdater(prevData.nodes)
                : newNodesOrUpdater,
          }
      );
    },
    [updateData]
  );

  const handlePauseUpdate = useCallback(
    (
      rentWagon: RentWagonSell,
      pauseIndex: number,
      updatedPause: RentWagonSellPause
    ) => {
      const rent: IUpdateRentWagonSellPausesItem = {
        id: rentWagon.id,
        pauses: rentWagon.pauses.map((pause, index) =>
          index === pauseIndex ? updatedPause : pause
        ),
      };

      return bulkUpdatePauses([rent]);
    },

    [bulkUpdatePauses]
  );

  const handleShipWagons = () => {
    batchUpdate(() =>
      makeShipmentForPeriod(api, {
        wagons: selectedRentWagons.map(rentWagon => rentWagon.wagon),
        startDate: dateRangeFilterValue.startDate,
        endDate: dateRangeFilterValue.endDate,
        protocol: selectedRentWagons[0].protocol,
      }).then(refetch)
    );
  };

  const selectedRentWagons = selectedNodes
    .filter(
      (
        node
      ): node is TreeTableNode<
        RentWagonsTreeTableRentWagonNodeData<RentWagonSellListItem>
      > => node.data.kind === 'rentWagon'
    )
    .map(node => node.data.rentWagon);

  const allIsExpanded = nodes
    ? everyNode(nodes, node => !node.children || node.children.isExpanded)
    : false;

  React.useEffect(() => {
    setShipmentId(null);

    const supplierId = selectedRentWagons[0]?.supplier;
    const protocolId = selectedRentWagons[0]?.protocol;

    if (supplierId && nodes) {
      const supplier = nodes.find(
        (sup: TreeTableNode<RentWagonsTreeTableSupplierNodeData>) =>
          sup.id === `supplier-${supplierId}`
      );

      const foundedProtocol =
        supplier &&
        supplier.children?.nodes?.find(
          (protocolItem: TreeTableNode<RentWagonsTreeTableProtocolNodeData>) =>
            protocolItem.id === `protocol-${protocolId}`
        );

      const protocolNodes = foundedProtocol?.children?.nodes;
      const isAllSelected = protocolNodes?.every(
        (
          wagon: TreeTableNode<
            RentWagonsTreeTableRentWagonNodeData<RentWagonSellListItem>
          >
        ) => selectedRentWagons.some(wag => wag.id === wagon.data.rentWagon.id)
      );

      const hasOnlyCurrentProtocolWagons =
        protocolNodes?.length === selectedRentWagons.length && isAllSelected;

      const foundedProtocolData =
        foundedProtocol?.data as RentWagonsTreeTableProtocolNodeData;

      if (hasOnlyCurrentProtocolWagons) {
        setShipmentId(foundedProtocolData?.shipment || null);
      }

      setShipmentButtonActive(!hasOnlyCurrentProtocolWagons);
    }

    if (!supplierId) {
      setShipmentButtonActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodes]);

  return (
    <VGrid stretch>
      <FormattedTitle>Вагоны исходящие</FormattedTitle>

      <Row>
        <Grid>
          <Col span={3}>
            <SearchForm
              initialValue={route.params.search}
              onApply={search => {
                applyFilterParams({ search });
              }}
            />
          </Col>

          <Col span={9}>
            <Toolbar align="right">
              {data && (
                <DateRangeWithMonthSelectFilter
                  minDate={dayjs(data.meta.dateRange[0])
                    .startOf('month')
                    .toDate()}
                  maxDate={dayjs(data.meta.dateRange[1])
                    .add(1, 'month')
                    .startOf('month')
                    .toDate()}
                  initialValue={dateRangeFilterValue}
                  onApply={newDateRangeFilterValue => {
                    applyFilterParams(
                      newDateRangeFilterValue
                        ? {
                            fromDate: dayjs(
                              newDateRangeFilterValue.startDate
                            ).format(DATE_FORMAT_API_DATE),
                            toDate: dayjs(
                              newDateRangeFilterValue.endDate
                            ).format(DATE_FORMAT_API_DATE),
                          }
                        : {
                            fromDate: undefined,
                            toDate: undefined,
                          }
                    );
                  }}
                />
              )}

              <WagonNumbersFilter
                initialValue={route.params.wagonNumbers}
                onApply={wagonNumbers => {
                  applyFilterParams({ wagonNumbers });
                }}
              />

              <LinkButton
                options={{ reload: true }}
                params={route.params}
                text="Обновить"
                to={route.name}
              />

              <LinkButton text="Сбросить фильтры" to={route.name} />
            </Toolbar>
          </Col>
        </Grid>
      </Row>

      <Row>
        <Grid>
          <Col span={9}>
            <Toolbar valign="center">
              <span>Общее количество вагонов:</span>
              {!data ? <Spinner size={14} /> : data.meta.rentsCount}
            </Toolbar>
          </Col>

          <Col span={3}>
            <Toolbar align="right">
              <Switch
                checked={showInactive}
                label="Показывать выведенные из аренды"
                onChange={newShowInactive => {
                  setInactive(prevParam => !prevParam);
                  applyFilterParams({
                    showInactive: newShowInactive ? 'True' : undefined,
                  });
                }}
              />
            </Toolbar>
          </Col>
        </Grid>
      </Row>

      <Row>
        <Toolbar valign="center">
          <span>Выбрано вагонов: {selectedRentWagons.length}</span>

          <Button
            intent={Intent.SUCCESS}
            onClick={() => {
              acceptDialog.open({
                initialValues: {
                  protocol: null,
                  startDate: new Date(),
                  supplier: null,
                  wagons: '',
                },
              });
            }}
            text="Сдать в аренду"
          />

          <Button
            intent={Intent.SUCCESS}
            onClick={() => {
              closeDialog.open({
                initialValues: {
                  actFile: null,

                  wagons: uniq(
                    selectedRentWagons.map(rentWagon => rentWagon.wagon)
                  ).join(' '),

                  endDate: new Date(),
                },
              });
            }}
            text="Принять из аренды"
          />

          {shipmentId ? (
            <LinkButton
              text="Отгружен"
              to="shipmentInfo.edit"
              target="_blank"
              params={{ id: shipmentId }}
              intent={Intent.SUCCESS}
            />
          ) : (
            <Tooltip2 content="Выберите все вагоны в одном протоколе и месяц отгрузки.">
              <AnchorButton
                disabled={isShipmentButtonActive}
                intent={Intent.PRIMARY}
                onClick={handleShipWagons}
                text="Отгрузить"
              />
            </Tooltip2>
          )}

          {/* <Button */}
          {/*  disabled={isBatchUpdateInProgress || !anyRentWagonSelected} */}
          {/*  intent={Intent.SUCCESS} */}
          {/*  onClick={() => { */}
          {/*    batchUpdate(() => */}
          {/*      startRentWagonSellRepair( */}
          {/*        api, */}
          {/*        selectedRentWagons.map(rentWagon => rentWagon.id) */}
          {/*      ).then(refetch) */}
          {/*    ); */}
          {/*  }} */}
          {/*  text="Отправить в ремонт" */}
          {/* /> */}

          {/* <Button */}
          {/*  disabled={isBatchUpdateInProgress || !anyRentWagonSelected} */}
          {/*  intent={Intent.SUCCESS} */}
          {/*  onClick={() => { */}
          {/*    batchUpdate(() => */}
          {/*      stopRentWagonSellRepair( */}
          {/*        api, */}
          {/*        selectedRentWagons.map(rentWagon => rentWagon.id) */}
          {/*      ).then(refetch) */}
          {/*    ); */}
          {/*  }} */}
          {/*  text="Принять из ремонта" */}
          {/* /> */}

          {/* <Button */}
          {/*  disabled={isBatchUpdateInProgress || !anyRentWagonSelected} */}
          {/*  intent={Intent.SUCCESS} */}
          {/*  onClick={() => { */}
          {/*    batchUpdate(() => */}
          {/*      stopRentWagonSellCalculation( */}
          {/*        api, */}
          {/*        selectedRentWagons.map(rentWagon => rentWagon.id) */}
          {/*      ).then(refetch) */}
          {/*    ); */}
          {/*  }} */}
          {/*  text="Остановить начисление" */}
          {/* /> */}

          {/* <Button */}
          {/*  disabled={isBatchUpdateInProgress || !anyRentWagonSelected} */}
          {/*  intent={Intent.SUCCESS} */}
          {/*  onClick={() => { */}
          {/*    batchUpdate(() => */}
          {/*      startRentWagonSellCalculation( */}
          {/*        api, */}
          {/*        selectedRentWagons.map(rentWagon => rentWagon.id) */}
          {/*      ).then(refetch) */}
          {/*    ); */}
          {/*  }} */}
          {/*  text="Возобновить начисление" */}
          {/* /> */}
        </Toolbar>
      </Row>

      <Row>
        <Grid>
          <Col span={8}>
            <Toolbar>
              {allIsExpanded ? (
                <Button
                  icon="collapse-all"
                  text="Свернуть всё"
                  onClick={() => {
                    updateData(
                      prevData =>
                        prevData && {
                          ...prevData,
                          nodes: setAllNodesIsExpanded(prevData.nodes, false),
                        }
                    );
                  }}
                />
              ) : (
                <Button
                  icon="expand-all"
                  text="Развернуть всё"
                  onClick={() => {
                    updateData(
                      prevData =>
                        prevData && {
                          ...prevData,
                          nodes: setAllNodesIsExpanded(prevData.nodes, true),
                        }
                    );
                  }}
                />
              )}

              <LegendItem
                backgroundColor={RentWagonColor.NOT_RENTED}
                label="не в аренде"
              />

              <LegendItem
                backgroundColor={RentWagonColor.RENTED}
                label="в аренде"
              />

              <LegendItem
                backgroundColor={RentWagonColor.PAUSED}
                label="пауза"
              />

              <LegendItem
                backgroundColor={RentWagonColor.IN_REPAIR}
                label="в ремонте"
              />
            </Toolbar>
          </Col>

          <Col span={4}>
            <Toolbar align="right">
              <Button
                disabled={!isPauseCreationAllowed}
                intent={Intent.PRIMARY}
                text="Пауза"
                onClick={handleCreatePausesClick}
              />

              <Button
                disabled={!isPauseCreationAllowed}
                intent={Intent.PRIMARY}
                text="Текущий ремонт"
                onClick={handleCreateRoutineRepairsClick}
              />

              <Button
                disabled={!isPauseCreationAllowed}
                intent={Intent.PRIMARY}
                text="Деповский ремонт"
                onClick={handleCreateScheduledRepairsClick}
              />

              <Button
                disabled={!isPauseEditingAllowed}
                intent={Intent.SUCCESS}
                text="Редактировать"
                onClick={handleEditPausesClick}
              />

              <Button
                disabled={!isPauseRemovalAllowed}
                intent={Intent.DANGER}
                text="Удалить"
                onClick={handleRemovePausesClick}
              />
            </Toolbar>
          </Col>
        </Grid>
      </Row>

      <Row stretch>
        {!nodes ? (
          isFetching ? (
            <CenteredSpinner />
          ) : (
            <GenericErrorMessage />
          )
        ) : nodes.length === 0 ? (
          <EmptyListMessage />
        ) : (
          <RentWagonsTreeTable
            baseSegment="rentWagons.sell"
            contractsBaseSegment="contracts.sell"
            endDate={dateRangeToRender.endDate}
            fetchChildNodes={fetchChildNodes}
            isFetching={isFetching}
            nodes={nodes}
            selectedNodes={selectedNodes}
            selectedRegions={selectedRegions}
            startDate={dateRangeToRender.startDate}
            onNodesChange={handleNodesChange}
            onPauseUpdate={handlePauseUpdate}
            onSelectedNodesChange={setSelectedNodes}
            onSelection={setSelectedRegions}
          />
        )}
      </Row>

      {editPausesDialogForm.state && (
        <EditPausesDialogForm
          initialValues={editPausesDialogForm.state.initialValues}
          isOpen={editPausesDialogForm.state.isOpen}
          onClose={editPausesDialogForm.close}
          onClosed={editPausesDialogForm.destroy}
          onSubmit={async values => {
            try {
              const rents: IUpdateRentWagonSellPausesItem[] = [];

              if (!nodes) {
                return;
              }

              pausesForSelectedRegions.forEach(({ found }) => {
                if (!found) {
                  return;
                }

                const rentWagonNode = findNode(
                  nodes,
                  (
                    node
                  ): node is TreeTableNode<
                    RentWagonsTreeTableNodeData<RentWagonSellListItem> & {
                      kind: 'rentWagon';
                    }
                  > =>
                    node.data.kind === 'rentWagon' &&
                    node.data.rentWagon.id === found.rentWagonId
                );

                if (!rentWagonNode) {
                  return;
                }

                const { rentWagon } = rentWagonNode.data;

                rents.push({
                  id: rentWagon.id,
                  pauses: rentWagon.pauses.map((pause, pauseIndex) => {
                    if (found.pauseIndices.includes(pauseIndex)) {
                      return new RentWagonSellPause({
                        ...pause,
                        note: values.note,
                      });
                    }

                    return pause;
                  }),
                });
              });

              await bulkUpdatePauses(rents);

              editPausesDialogForm.close();
              return undefined;
            } catch (err) {
              return submissionErrorsFromApiError(
                err,
                'Не удалось отредактировать паузы: Непредвиденная ошибка'
              );
            }
          }}
        />
      )}

      {acceptDialog.state && (
        <RentWagonsSellAcceptDialogForm
          initialValues={acceptDialog.state.initialValues}
          isOpen={acceptDialog.state.isOpen}
          onClose={acceptDialog.close}
          onClosed={acceptDialog.destroy}
          onSubmit={async ({ protocol, startDate, supplier, wagons }) => {
            try {
              await acceptRentWagonSellWagons(api, {
                protocol: protocol == null ? null : Number(protocol),
                startDate,
                supplier,
                wagons: wagons.trim().split(/\s+/),
              });

              refetch();
              acceptDialog.close();
              return undefined;
            } catch (err) {
              return submissionErrorsFromApiError(
                err,
                'Не удалось сдать вагоны в аренду: Непредвиденная ошибка'
              );
            }
          }}
        />
      )}

      {closeDialog.state && (
        <RentWagonsSellCloseDialogForm
          initialValues={closeDialog.state.initialValues}
          isOpen={closeDialog.state.isOpen}
          onClose={closeDialog.close}
          onClosed={closeDialog.destroy}
          onSubmit={async ({ actFile, endDate, wagons }) => {
            try {
              await closeRentWagonSellWagons(api, {
                actFile,
                endDate,
                wagons: wagons.trim().split(/\s+/),
              });

              refetch();
              closeDialog.close();

              return undefined;
            } catch (err) {
              return submissionErrorsFromApiError(
                err,
                'Не удалось принять вагоны из аренды: Непредвиденная ошибка'
              );
            }
          }}
        />
      )}
    </VGrid>
  );
}
